import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api from "@/lib/api";
import { Switch } from "@/components/ui/switch";
import { BellSimpleRinging, Trash, MagnifyingGlass } from "@phosphor-icons/react";

function criteriaLabel(c) {
  const parts = [];
  if (c.q) parts.push(`« ${c.q} »`);
  if (c.family) parts.push(c.family);
  if (c.seniority) parts.push(c.seniority);
  if (c.city) parts.push(c.city);
  if (c.availability) parts.push(c.availability === "AVAILABLE_NOW" ? "Disponible" : "Bientôt");
  if (c.remote) parts.push(c.remote);
  return parts.length ? parts.join(" · ") : "Tous les profils";
}

export default function MarketplaceAlerts() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try { const { data } = await api.get("/marketplace/saved-searches"); setItems(data.items || []); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const patch = async (s, upd) => {
    try {
      const { data } = await api.patch(`/marketplace/saved-searches/${s.id}`, upd);
      setItems((x) => x.map((y) => y.id === s.id ? data : y));
    } catch { toast.error("Action impossible"); }
  };
  const del = async (s) => {
    try { await api.delete(`/marketplace/saved-searches/${s.id}`); setItems((x) => x.filter((y) => y.id !== s.id)); toast.success("Alerte supprimée"); }
    catch { toast.error("Suppression impossible"); }
  };

  return (
    <div data-testid="marketplace-alerts">
      <div className="overline mb-2 flex items-center gap-2"><BellSimpleRinging size={14} className="text-accent" /> Mes alertes</div>
      <h1 className="font-display text-3xl font-bold">Recherches enregistrées</h1>
      <p className="text-muted text-sm mt-1 mb-6">Recevez un email quotidien et une alerte instantanée dès qu'un consultant correspond à vos critères.</p>

      {loading ? (
        <div className="text-muted font-mono text-sm animate-pulse">Chargement…</div>
      ) : items.length === 0 ? (
        <div className="surface rounded-lg p-12 text-center" data-testid="mk-alerts-empty">
          <MagnifyingGlass size={40} weight="duotone" className="text-accent mx-auto mb-3" />
          <div className="font-display font-bold text-fg mb-1">Aucune alerte enregistrée</div>
          <p className="text-muted text-sm">Depuis l'onglet Profils, lancez une recherche puis cliquez sur « Enregistrer cette recherche ».</p>
        </div>
      ) : (
        <div className="space-y-3" data-testid="mk-alerts-list">
          {items.map((s) => (
            <div key={s.id} className="surface rounded-lg p-4 flex items-center justify-between gap-4" data-testid={`mk-alert-${s.id}`}>
              <div className="min-w-0">
                <div className="font-display font-bold text-fg truncate">{s.name}</div>
                <div className="text-xs text-muted mt-0.5">{criteriaLabel(s.criteria)}</div>
              </div>
              <div className="flex items-center gap-5 shrink-0">
                <label className="flex items-center gap-2 text-xs text-muted">
                  <Switch checked={s.alerts_enabled} onCheckedChange={(v) => patch(s, { alerts_enabled: v })} data-testid={`mk-alert-daily-${s.id}`} />
                  Digest quotidien
                </label>
                <label className="flex items-center gap-2 text-xs text-muted">
                  <Switch checked={s.instant} onCheckedChange={(v) => patch(s, { instant: v })} data-testid={`mk-alert-instant-${s.id}`} />
                  Alerte instantanée
                </label>
                <button onClick={() => del(s)} data-testid={`mk-alert-del-${s.id}`} className="text-red-400 hover:text-red-300 p-1"><Trash size={16} /></button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
